import { useMemo, useState } from 'react';
import { Star, X } from 'lucide-react';
import { NUTRIENTS } from '@/engine/nutrients';
import { scaleNutrients } from '@/engine/portions';
import type { MealSlot } from '@/engine/types';
import { EstimateMicros } from '@/features/foods/EstimateMicros';
import { FoodProvenance } from '@/features/foods/FoodProvenance';
import type { FoodRow } from '@/lib/db';
import { num } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toggleFavorite } from '@/lib/foods';
import { rememberPackageWeight } from '@/lib/off/lookup';
import { upsertCatalogFood } from '@/lib/useCatalog';
import { PackageFractions } from './PackageFractions';
import { addMealEntry, defaultSlot, SLOTS } from './useDiary';

interface Props {
  food: FoodRow;
  date: string;
  initialSlot?: MealSlot;
  onClose: () => void;
  onAdded?: () => void;
}

export function QuantitySheet({ food, date, initialSlot, onClose, onAdded }: Props) {
  const [slot, setSlot] = useState<MealSlot>(initialSlot ?? defaultSlot());
  const [grams, setGrams] = useState<number>(() => defaultGrams(food));
  const [saving, setSaving] = useState(false);
  const [packageGrams, setPackageGrams] = useState<number | null>(food.packageGrams ?? null);
  // Estimar micros cambia el alimento aquí mismo: lo que se registre tiene que
  // ser lo que se está viendo.
  const [current, setCurrent] = useState(food);
  const [estimating, setEstimating] = useState(false);

  // `> 0` y no `|| 0`: con "-5" a medio teclear, el motor lanzaba en pleno render.
  const preview = useMemo(
    () => scaleNutrients(current.per100g, grams > 0 ? grams : 0),
    [current, grams],
  );

  const chips = useMemo(() => {
    const out: { label: string; grams: number }[] = [];
    if (food.servingGrams) out.push({ label: 'Porción', grams: food.servingGrams });
    for (const p of food.householdPortions.slice(0, 3)) {
      out.push({ label: `1 ${p.label}`, grams: p.grams });
    }
    for (const g of [30, 100, 150]) {
      if (!out.some((c) => c.grams === g)) out.push({ label: `${g} g`, grams: g });
    }
    return out.slice(0, 6);
  }, [food]);

  async function add() {
    if (saving || !(grams > 0)) return;
    setSaving(true);
    try {
      await addMealEntry({ date, slot, food: current, grams });
      onAdded?.();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Añadir ${food.name}`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="safe-bottom w-full max-w-lg rounded-t-3xl border-t border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg leading-tight font-semibold">{current.name}</h2>
          <div className="flex shrink-0 items-center gap-1">
            {/* Los favoritos son el atajo de lo que se come a diario (SPEC 6.B):
                se marcan aquí, que es donde ya estás al registrarlo. */}
            <button
              type="button"
              aria-pressed={current.favorite === true}
              aria-label={current.favorite ? 'Quitar de favoritos' : 'Marcar como favorito'}
              onClick={() => {
                void toggleFavorite(current.id).then((updated) => {
                  if (updated) {
                    setCurrent(updated);
                    upsertCatalogFood(updated);
                  }
                });
              }}
              className="tap -mt-1 rounded-lg px-2 text-[var(--color-text-muted)]"
            >
              <Star
                aria-hidden="true"
                className={cn(
                  'size-5',
                  current.favorite && 'fill-[var(--color-brand)] text-[var(--color-brand)]',
                )}
              />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="tap -mt-1 -mr-1 rounded-lg px-2 text-[var(--color-text-muted)]"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </div>
        </div>

        {/* Entero/Medio/Cuarto valen para cualquier cosa que venga en envase,
            sea de Open Food Facts o dada de alta a mano. No valen para un
            genérico como "arroz crudo", que no tiene envase que partir. */}
        {food.kind === 'producto' || food.packageGrams != null ? (
          <div className="mt-3">
            <PackageFractions
              packageGrams={packageGrams}
              selected={grams}
              onPick={setGrams}
              onRememberWeight={(value) => {
                setPackageGrams(value);
                void rememberPackageWeight(food.id, value);
              }}
            />
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <button
              key={`${chip.label}-${chip.grams}`}
              type="button"
              onClick={() => setGrams(chip.grams)}
              className={cn(
                'tap rounded-full border px-3 text-sm font-medium',
                grams === chip.grams
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <label htmlFor="gramos" className="text-sm font-medium">
            Gramos
          </label>
          <input
            id="gramos"
            type="number"
            inputMode="numeric"
            min={1}
            max={5000}
            value={grams}
            onChange={(e) => setGrams(Number(e.target.value))}
            className="tap w-28 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 text-right text-base tabular"
          />
          <p className="flex-1 text-right text-sm text-[var(--color-text-muted)] tabular">
            {preview.kcal == null ? '—' : `${num(preview.kcal)} kcal`} ·{' '}
            {preview.protein == null ? '—' : `${num(preview.protein, 1)} g prot.`}
          </p>
        </div>

        <fieldset className="mt-4">
          <legend className="mb-1.5 text-sm font-medium">Franja</legend>
          <div className="flex flex-wrap gap-2">
            {SLOTS.map((s) => (
              <button
                key={s.value}
                type="button"
                role="radio"
                aria-checked={slot === s.value}
                onClick={() => setSlot(s.value)}
                className={cn(
                  'tap rounded-full border px-3 text-sm font-medium',
                  slot === s.value
                    ? 'border-[var(--color-brand)] bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]'
                    : 'border-[var(--color-border)] text-[var(--color-text-muted)]',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </fieldset>

        <button
          type="button"
          onClick={() => void add()}
          disabled={saving || !(grams > 0)}
          className="tap mt-5 w-full rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
        >
          Añadir
        </button>

        {/* Un producto que no declara micros hunde la cobertura del día. El
            usuario puede prestarle los de un genérico parecido (SPEC 5.4). */}
        {current.kind === 'producto' &&
        current.estimatedFrom == null &&
        missingMicros(current) > 0 ? (
          estimating ? (
            <EstimateMicros
              food={current}
              onDone={(updated) => {
                setCurrent(updated);
                setEstimating(false);
              }}
              onClose={() => setEstimating(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEstimating(true)}
              className="tap mt-3 w-full rounded-xl border border-dashed border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-muted)]"
            >
              Estimar los micros que faltan
            </button>
          )
        ) : null}

        <FoodProvenance food={current} />
      </div>
    </div>
  );
}

/**
 * Cuántos micros de seguimiento semanal no trae el alimento. Los macros no
 * cuentan: un producto sin proteína declarada es un dato mal metido, no un
 * caso de estimación.
 */
function missingMicros(food: FoodRow): number {
  return NUTRIENTS.filter((n) => n.window === 'semana' && food.per100g[n.key] == null).length;
}

/** Porción declarada, si no la primera doméstica, si no 100 g. */
function defaultGrams(food: FoodRow): number {
  if (food.servingGrams && food.servingGrams > 0) return food.servingGrams;
  const first = food.householdPortions[0];
  if (first) return first.grams;
  return 100;
}
